﻿import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Box,
  Typography,
  Autocomplete,
  TextField,
} from "@mui/material";
import { Close as CloseIcon } from "@mui/icons-material";
import "../../AddCrossReferencePopup.css";
import { ShepButton } from "@/components/GenericComponents/ShepComponents";
import axios from "@/features/axiosHelper";
import { useDispatch } from "react-redux";
import { createAlert } from "@/features/homepage/alertSliceList";
import { useWorkflowLookups } from "../WorkflowLookupsContext";
import { WorkflowLookupMode } from "@/components/Maintenance/MaintenanceTypes";

const labelSx = { mr: 3, minWidth: 200, alignItems: "right" };

const AddThreePLQualifiersPopup = ({
  open,
  onClose,
  workflowLookupType,
  fetchData,
  mode = WorkflowLookupMode.Add,
  existingRow = null,
}) => {
  const dispatch = useDispatch();

  // Use context for customers, qualifiers, and loading
  const {
    customersLoaded,
    itemqualifiers,
    getItemQualifiers,
    rows,
    customerConnections,
  } = useWorkflowLookups();

  // Only track the selected customer connection object
  const [selectedCustomerConnection, setSelectedCustomerConnection] =
    useState(null);
  const [codeFieldsEnabled, setCodeFieldsEnabled] = useState(false);
  const [Code1, setCode1] = useState("");
  const [Code2, setCode2] = useState("");
  const [Code3, setCode3] = useState("");
  const [Code4, setCode4] = useState("");
  const [Code5, setCode5] = useState("");

  const [qualifiersLoading, setQualifiersLoading] = useState(false);

  // Prefill when editing
  useEffect(() => {
    if (!open) return;

    if (mode === WorkflowLookupMode.Edit && existingRow) {
      const conn =
        customerConnections.find(
          (c) => c.ConnectionId === existingRow.CustomerConnectionId
        ) || null;

      setSelectedCustomerConnection(conn);
      setCode1(existingRow.Code1 || "");
      setCode2(existingRow.Code2 || "");
      setCode3(existingRow.Code3 || "");
      setCode4(existingRow.Code4 || "");
      setCode5(existingRow.Code5 || "");

      if (conn?.ConnectionId) {
        (async () => {
          setQualifiersLoading(true);
          try {
            await getItemQualifiers(conn.ConnectionId, -1);
            setCodeFieldsEnabled(true);
          } finally {
            setQualifiersLoading(false);
          }
        })();
      } else {
        setCodeFieldsEnabled(false);
      }
    } else if (mode === WorkflowLookupMode.Add) {
      // ensure clean slate
      setSelectedCustomerConnection(null);
      setCode1("");
      setCode2("");
      setCode3("");
      setCode4("");
      setCode5("");
      setCodeFieldsEnabled(false);
    }
  }, [open]);

  // Fetch item qualifiers when customer changes
  useEffect(() => {
    if (open && selectedCustomerConnection?.ConnectionId) {
      setQualifiersLoading(true);
      Promise.resolve(
        getItemQualifiers(selectedCustomerConnection.ConnectionId, -1)
      ).finally(() => setQualifiersLoading(false));
      setCodeFieldsEnabled(true);
    } else {
      setCodeFieldsEnabled(false);
      setQualifiersLoading(false);
    }
  }, [open, selectedCustomerConnection, getItemQualifiers]);

  const validateForm = () => {
    if (!selectedCustomerConnection?.ConnectionId) {
      return {
        isValid: false,
        message: "Please select a customer connection.",
      };
    }
    if (![Code1, Code2, Code3, Code4, Code5].some((c) => !!c)) {
      return {
        isValid: false,
        message: "Please select at least one qualifier code before saving.",
      };
    }
    return { isValid: true };
  };

  const handleSubmit = async () => {
    const validation = validateForm();
    if (!validation.isValid) {
      dispatch(
        createAlert({
          status: "Error",
          message: validation.message,
        })
      );
      return;
    }

    const payload = {
      workflowLookupType,
      Code1,
      Code2,
      Code3,
      Code4,
      Code5,
      CustomerConnectionId: selectedCustomerConnection?.ConnectionId ?? null,
      CustomerConnectionName: selectedCustomerConnection?.Name ?? "",
    };

    try {
      // Edit mode, update existing record directly
      if (mode === WorkflowLookupMode.Edit && existingRow?.WorkflowLookupId) {
        await axios.put(
          `/workflowlookups/${existingRow.WorkflowLookupId}`,
          payload
        );
      } else {
        const normalize = (v) =>
          v === null || v === undefined || v === 0 ? "" : String(v);

        const matchingExistingRow = rows.find(
          (x) =>
            x.WorkflowLookupType == workflowLookupType &&
            normalize(x.CustomerConnectionId) ===
              normalize(selectedCustomerConnection?.ConnectionId)
        );

        if (matchingExistingRow && matchingExistingRow.WorkflowLookupId) {
          const userConfirmed = await new Promise((resolve) => {
            dispatch(
              createAlert({
                status: "Warning",
                alertType: "result",
                title: "Update Overlapping Record",
                message:
                  "A lookup for this customer connection already exists. Do you want to update it?",
                buttonLabel: "Update",
                onButtonClick: () => resolve(true),
                buttonLabel2: "Cancel",
                onButtonClick2: () => resolve(false),
              })
            );
          });
          if (!userConfirmed) {
            // Do not save, do not close
            return;
          }
          await axios.put(
            `/workflowlookups/${matchingExistingRow.WorkflowLookupId}`,
            payload
          );
        } else {
          await axios.post("/workflowlookups", payload);
        }
      }

      if (fetchData) fetchData();
      handleClose();
    } catch (e) {
      dispatch(
        createAlert({
          status: "Error",
          message: e.message,
        })
      );
    }
  };

  const handleClose = () => {
    setCode1("");
    setCode2("");
    setCode3("");
    setCode4("");
    setCode5("");
    setSelectedCustomerConnection(null);
    setCodeFieldsEnabled(false);
    onClose();
  };

  // Helper for code-first label
  const codeFirstLabel = (code, desc) => {
    if (!code) return desc || "";
    if (desc && desc !== "" && desc !== code) return `${code} - ${desc}`;
    return code;
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth={"md"}
      fullWidth={true}
      PaperProps={{ style: { borderRadius: 15 } }}
    >
      <DialogTitle
        className="add-crossref-dialog-title"
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          backgroundColor: "var(--primary-main)",
          color: "var(--default-light)",
          fontSize: "24px",
          borderRadius: "15px 15px 0 0",
          padding: "16px 24px",
        }}
      >
        <div style={{ fontWeight: "var(--font-semi-bold)" }}>
          {mode === WorkflowLookupMode.Edit
            ? "Edit 3PL Qualifiers"
            : "Add 3PL Qualifiers"}
        </div>
        <IconButton
          className="closeIconButton"
          style={{ color: "var(--default-light)" }}
          onClick={handleClose}
          size="large"
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent
        className="dialogContent"
        style={{
          backgroundColor: "var(--disabled)",
          padding: "35px",
          minWidth: 400,
        }}
      >
        <Box sx={{ ml: 3 }}>
          {workflowLookupType === 0 && (
            <>
              <Box display="flex" alignItems="center" mb={1}>
                <Typography variant="body1" sx={labelSx}>
                  Customer Connection:
                </Typography>
                <Autocomplete
                  options={customerConnections}
                  loading={!customersLoaded}
                  getOptionLabel={(opt) =>
                    typeof opt === "string" ? opt : opt.Name
                  }
                  value={selectedCustomerConnection}
                  onChange={(event, newValue) => {
                    setSelectedCustomerConnection(newValue || null);
                    setCodeFieldsEnabled(!!newValue);
                  }}
                  disabled={mode === WorkflowLookupMode.Edit}
                  renderInput={(params) => (
                    <TextField
                      sx={{ minWidth: 350, height: 45 }}
                      {...params}
                      autoComplete="off"
                    />
                  )}
                />
              </Box>
              {[1, 2, 3, 4, 5].map((num) => {
                const selectedQualifiers = [
                  Code1,
                  Code2,
                  Code3,
                  Code4,
                  Code5,
                ].filter((val, idx) => val && idx !== num - 1);
                const availableOptions = itemqualifiers.filter(
                  (q) => !selectedQualifiers.includes(q.Qualifier)
                );

                return (
                  <Box
                    display="flex"
                    alignItems="center"
                    mb={1}
                    key={`code${num}`}
                  >
                    <Typography
                      variant="body1"
                      sx={labelSx}
                    >{`Code ${num}:`}</Typography>
                    <Autocomplete
                      options={availableOptions}
                      getOptionLabel={(option) =>
                        option
                          ? codeFirstLabel(option.Qualifier, option.Description)
                          : ""
                      }
                      value={
                        itemqualifiers.find(
                          (q) =>
                            q.Qualifier ===
                            [Code1, Code2, Code3, Code4, Code5][num - 1]
                        ) || null
                      }
                      onChange={(_, newValue) => {
                        const val = newValue ? newValue.Qualifier : "";
                        switch (num) {
                          case 1:
                            setCode1(val);
                            break;
                          case 2:
                            setCode2(val);
                            break;
                          case 3:
                            setCode3(val);
                            break;
                          case 4:
                            setCode4(val);
                            break;
                          case 5:
                            setCode5(val);
                            break;
                          default:
                            break;
                        }
                      }}
                      isOptionEqualToValue={(option, value) =>
                        option?.Qualifier === value?.Qualifier
                      }
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          sx={{ minWidth: 350, height: 45 }}
                          autoComplete="off"
                        />
                      )}
                      loading={qualifiersLoading}
                      loadingText="Loading..."
                      noOptionsText={
                        selectedCustomerConnection
                          ? "No valid qualifiers"
                          : "Please select a connection"
                      }
                      disabled={!codeFieldsEnabled}
                    />
                  </Box>
                );
              })}
            </>
          )}
        </Box>
      </DialogContent>
      <DialogActions style={{ padding: "16px 24px" }}>
        <ShepButton variant="secondary" onClick={handleClose}>
          Cancel
        </ShepButton>
        <ShepButton onClick={handleSubmit}>
          {mode === WorkflowLookupMode.Edit ? "Save" : "Add"}
        </ShepButton>
      </DialogActions>
    </Dialog>
  );
};

export default AddThreePLQualifiersPopup;
